// Session-only, roomId-keyed store of RAW game-log events for the LAST FEW hands. Events are stored
// raw (not pre-formatted) so names/pup-names resolve at render time from live game state + the
// catalog — a late username or a catalog self-heal retroactively fixes old lines, and the identical
// raw array feeds both the live sidebar and the replay viewer. No persistence: nothing is retained
// beyond the session (the store is memory-only and cleared on leave/logout).
//
// Retention: a rolling window of the most recent MAX_HANDS hands per room (`byRoom[id].hands`,
// oldest→newest). A joiner therefore accumulates the in-progress hand (backfilled current-hand-only
// on open) plus every hand after it, and never a hand that COMPLETED before they joined — the window
// only ever drops the OLDEST hand. A page refresh rebuilds from the current hand (no multi-hand DB
// backfill, by design — that is what keeps the join cutoff correct).
//
// Hand identity: a hand's `seq` is per-hand (resets to 1 each hand; see EventLogService), so `seq`
// alone can't tell a NEW hand's HAND_START from a re-delivered one (both seq 1). We key each hand
// bucket on its HAND_START's `ts` instead: a matching ts => re-delivery (dedup into that bucket);
// no match => a new hand (push a fresh bucket). A provisional bucket (mid-hand open, events before
// any HAND_START) adopts the first HAND_START's ts and merges rather than spawning a duplicate.

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchWithToken } from '../utilities/fetchWithToken';
import { BASE_URL } from '../utilities/BASE_URL';

const MAX_EVENTS = 500; // defensive per-hand bound; a hand has far fewer events
const MAX_HANDS = 5;    // rolling window: keep only the most recent 5 hands per room

const freshBucket = (handStartTs = null) => ({ handStartTs, seenSeq: {}, events: [] });

// Fetch a hand's events (GET /games/{id}/handhistory?hand=N|previous) and feed them through the same
// ingest path as the live push. Dedup makes it idempotent, so backfilling a hand already partly seen
// live is safe (used on table open + reconnect to heal a mid-hand gap).
export const fetchHandHistory = createAsyncThunk(
  'actionLog/fetchHandHistory',
  async ({ roomId, hand }) => {
    const res = await fetchWithToken(`${BASE_URL}/games/${roomId}/handhistory?hand=${hand}`);
    if (!res.ok) throw new Error(`handhistory ${res.status}`);
    const events = await res.json();
    return { roomId, events: Array.isArray(events) ? events : [] };
  },
);

const insertEvent = (bucket, ev) => {
  if (bucket.seenSeq[ev.seq]) return; // dedup within the current hand
  bucket.seenSeq[ev.seq] = true;
  const arr = bucket.events;
  let i = arr.length;
  while (i > 0 && arr[i - 1].seq > ev.seq) i--; // keep seq-ordered (handles out-of-order channels)
  arr.splice(i, 0, ev);
  if (arr.length > MAX_EVENTS) {
    const dropped = arr.splice(0, arr.length - MAX_EVENTS);
    dropped.forEach((d) => delete bucket.seenSeq[d.seq]);
  }
};

const ensureRoom = (state, key) => {
  if (!state.byRoom[key]) state.byRoom[key] = { hands: [] };
  return state.byRoom[key];
};

const ingest = (state, roomId, events) => {
  if (roomId == null || !Array.isArray(events)) return;
  const key = String(roomId);
  for (const ev of events) {
    if (!ev || typeof ev.type !== 'string' || typeof ev.seq !== 'number') continue;
    const entry = ensureRoom(state, key);
    if (ev.type === 'HAND_START') {
      // 1. Re-delivery of an already-retained hand (match by ts): dedup insert, keep newer events.
      const existing = ev.ts != null ? entry.hands.find((h) => h.handStartTs === ev.ts) : undefined;
      if (existing) {
        insertEvent(existing, ev);
        continue;
      }
      const newest = entry.hands[entry.hands.length - 1];
      // 2. A provisional newest bucket (mid-hand open) adopts this hand.
      if (newest && newest.handStartTs == null) {
        newest.handStartTs = ev.ts ?? null;
        insertEvent(newest, ev);
        continue;
      }
      // 3. Genuinely new hand: push a fresh bucket, drop the oldest beyond the rolling window.
      const bucket = freshBucket(ev.ts ?? null);
      entry.hands.push(bucket);
      if (entry.hands.length > MAX_HANDS) entry.hands.splice(0, entry.hands.length - MAX_HANDS);
      insertEvent(bucket, ev);
      continue;
    }
    // Non-HAND_START: attach to the newest bucket (create a provisional one on a mid-hand open).
    let newest = entry.hands[entry.hands.length - 1];
    if (!newest) {
      newest = freshBucket();
      entry.hands.push(newest);
    }
    insertEvent(newest, ev);
  }
};

const actionLogSlice = createSlice({
  name: 'actionLog',
  initialState: { byRoom: {} },
  reducers: {
    // Single ingest for both the live push (a 1-element array) and REST backfill.
    actionLogEventsReceived: (state, action) => {
      const { roomId, events } = action.payload || {};
      ingest(state, roomId, events);
    },
    actionLogCleared: (state, action) => {
      const { roomId } = action.payload || {};
      if (roomId != null) delete state.byRoom[String(roomId)];
    },
    actionLogAllCleared: (state) => {
      state.byRoom = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHandHistory.fulfilled, (state, action) => {
      const { roomId, events } = action.payload || {};
      ingest(state, roomId, events);
    });
  },
});

export const { actionLogEventsReceived, actionLogCleared, actionLogAllCleared } = actionLogSlice.actions;

// All retained hands for a room (oldest→newest), or undefined. A stable reference straight out of
// Redux state (never a freshly built array), so useSelector's default equality doesn't thrash.
export const selectActionLogHands = (state, roomId) => state.actionLog.byRoom[String(roomId)]?.hands;

// Back-compat: the NEWEST hand's events (what the single-bucket selector used to return).
export const selectActionLogEvents = (state, roomId) => {
  const hands = state.actionLog.byRoom[String(roomId)]?.hands;
  return hands && hands.length ? hands[hands.length - 1].events : undefined;
};

export default actionLogSlice.reducer;
